import { useJobAction } from "@/components/job/useJobAction";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { CostCodeReport } from "@/components/CostCodeReport";
import {
  DollarSign, Plus, TrendingUp, AlertTriangle, Pencil, Trash2, Sparkles, RefreshCw, X,
} from "lucide-react";

const nzd = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 0 }).format(n || 0);

const nzdCents = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);

const HEALTH_COLOR = {
  green: { bar: "#16a34a", ring: "ring-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "On track" },
  amber: { bar: "#d97706", ring: "ring-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Watch" },
  red:   { bar: "#dc2626", ring: "ring-red-500",   chip: "bg-red-50 text-red-700 border-red-200",       label: "Over" },
};

/**
 * Job Budget vs Actual panel.
 * Shown on the Job Detail page for office/admin/qs roles.
 * Field workers never see costs.
 */
export function JobBudgetPanel({ jobId, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [budget, setBudget] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [addCostOpen, setAddCostOpen] = useState(false);
  useJobAction("cost", () => setAddCostOpen(true), !!budget && !loading);
  useJobAction("material-cost", () => setAddCostOpen("materials"), !!budget && !loading);
  const [ver, setVer] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      const [b, c] = await Promise.all([
        api.get(`/jobs/${jobId}/budget`),
        api.get(`/jobs/${jobId}/costs`),
      ]);
      setBudget(b.data);
      setCosts(c.data || []);
      onChanged?.();
      setVer((v) => v + 1);
    } catch (e) {
      // If user isn't allowed, silently hide the panel
      if (e.response?.status === 403) setBudget(null);
      else toast.error(e.response?.data?.detail || "Failed to load budget");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [jobId]);

  if (loading) {
    return (
      <section className="hard-card p-5" data-testid={`job-budget-loading-${jobId}`}>
        <div className="label-tag mb-3 flex items-center gap-1.5">
          <DollarSign className="w-3 h-3" /> Budget vs Actual
        </div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>
      </section>
    );
  }
  if (!budget) return null;

  const hasBudget = (budget.total_budget || 0) > 0;
  const revised = budget.revised_budget || 0;
  const health = HEALTH_COLOR[budget.health || "green"];

  return (
    <section className="hard-card p-5" data-testid={`job-budget-panel-${jobId}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> Budget vs Actual
          </div>
          {budget.source && (
            <div className="text-[10px] mt-1 font-mono uppercase tracking-widest text-zinc-500">
              {budget.source === "quote" ? "seeded from quote " : "manual · "}
              {budget.quote_number || ""}
            </div>
          )}
          <div className="text-[11px] text-zinc-500 mt-1 max-w-md" data-testid={`budget-auto-hint-${jobId}`}>
            Spent pulls automatically from approved timesheets (labour, plant &amp; gear hours), materials and supplier invoices — only add manual costs for extras Fieldlink doesn&apos;t track.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" onClick={load}
            data-testid={`budget-refresh-${jobId}`}
            className="border-2 border-black h-8">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {isAdmin && (
            <Button
              size="sm" onClick={() => setEditOpen(true)}
              data-testid={`budget-edit-btn-${jobId}`}
              className="bg-black text-white hover:bg-zinc-800 h-8">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit budget
            </Button>
          )}
        </div>
      </div>

      {!hasBudget ? (
        <div className="border-2 border-dashed border-zinc-300 p-6 text-center">
          <div className="font-display font-bold text-lg text-zinc-800">No budget set</div>
          <div className="text-sm text-zinc-600 mt-1 mb-4">
            {isAdmin
              ? "Set a total budget to track spend and get 80/100/110% alerts."
              : "Ask an admin to set a budget for this job."}
          </div>
          {isAdmin && (
            <Button
              onClick={() => setEditOpen(true)}
              data-testid={`budget-setup-btn-${jobId}`}
              className="bg-black text-white hover:bg-zinc-800">
              <Sparkles className="w-4 h-4 mr-1" /> Set budget
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Header stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Stat label="Budget" value={nzd(budget.total_budget)} testid="budget-total" />
            <Stat
              label={budget.variations_uplift ? `+ Variations` : "Revised"}
              value={budget.variations_uplift ? `+ ${nzd(budget.variations_uplift)}` : nzd(revised)}
              subtle={budget.variations_uplift ? `= ${nzd(revised)}` : null}
              testid="budget-variations"
            />
            <Stat label="Spent" value={nzd(budget.total_actual)} testid="budget-spent" />
            <Stat
              label="Remaining"
              value={nzd(budget.total_remaining)}
              tint={budget.total_remaining < 0 ? "text-red-600" : "text-emerald-700"}
              testid="budget-remaining"
            />
          </div>

          {/* Health bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-widest border ${health.chip}`}
                      data-testid={`budget-health-${jobId}`}>
                  {health.label}
                </span>
                <span className="text-[11px] font-mono text-zinc-500">
                  {budget.pct_spent != null ? `${budget.pct_spent}% spent` : "No spend yet"}
                </span>
              </div>
              {budget.alerts_fired?.length > 0 && (
                <span className="text-[10px] flex items-center gap-1 text-red-700 font-bold uppercase tracking-widest">
                  <AlertTriangle className="w-3 h-3" />
                  Alerts fired: {budget.alerts_fired.join("%, ")}%
                </span>
              )}
            </div>
            <div className="h-3 w-full bg-zinc-100 border border-zinc-200 relative overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${Math.min(budget.pct_spent || 0, 100)}%`,
                  background: health.bar,
                }}
              />
              {/* Threshold markers */}
              {[80, 100].map((pct) => (
                <span
                  key={pct}
                  className="absolute top-0 h-full border-l border-dashed border-zinc-500/40"
                  style={{ left: `${pct}%` }}
                  title={`${pct}% threshold`}
                />
              ))}
            </div>
            {(budget.pct_spent || 0) > 100 && (
              <div className="text-[11px] text-red-700 font-bold mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {nzd(Math.abs(budget.total_remaining))} over the revised budget.
              </div>
            )}
          </div>

          {/* Category rows */}
          <div className="space-y-3">
            {budget.categories.map((cat) => (
              <CategoryRow key={cat.key} cat={cat} />
            ))}
          </div>

          {/* Costs table */}
          <div className="mt-6 pt-5 border-t-2 border-black/10">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tag flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> Manual cost entries ({costs.length})
              </div>
              <Button
                size="sm" onClick={() => setAddCostOpen(true)}
                data-testid={`budget-add-cost-btn-${jobId}`}
                className="bg-black text-white hover:bg-zinc-800 h-8">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add cost
              </Button>
            </div>
            {costs.length === 0 ? (
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                No manual entries yet — automatic actuals from timesheets, materials & AI-scanned supplier invoices roll in here.
              </div>
            ) : (
              <div className="divide-y divide-zinc-100 border border-zinc-200">
                {costs.map((c) => (
                  <CostRow key={c.id} cost={c} jobId={jobId} categories={budget.categories} onChange={load} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <CostCodeReport jobId={jobId} version={ver} />

      {editOpen && (
        <EditBudgetDialog
          jobId={jobId} budget={budget}
          onClose={() => setEditOpen(false)}
          onSaved={(b) => { setBudget(b); setEditOpen(false); onChanged?.(); }}
        />
      )}
      {addCostOpen && (
        <AddCostDialog
          jobId={jobId} categories={budget.categories} initialCategory={addCostOpen === "materials" ? "materials" : undefined}
          onClose={() => setAddCostOpen(false)}
          onSaved={() => { setAddCostOpen(false); load(); }}
        />
      )}
    </section>
  );
}

function Stat({ label, value, subtle, tint, testid }) {
  return (
    <div className="bg-white border-2 border-zinc-100 p-3" data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-bold text-lg mt-0.5 ${tint || "text-zinc-900"}`}>{value}</div>
      {subtle && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{subtle}</div>}
    </div>
  );
}

function CategoryRow({ cat }) {
  const pct = cat.pct_spent == null ? 0 : Math.min(cat.pct_spent, 100);
  const over = (cat.pct_spent || 0) > 100;
  const barColor = over ? "#dc2626" : (cat.pct_spent >= 80 ? "#d97706" : "#16a34a");
  return (
    <div className="text-sm" data-testid={`budget-cat-${cat.key}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-bold">{cat.label}</span>
          {cat.auto_portion > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 text-zinc-500 uppercase tracking-widest">
              auto {nzd(cat.auto_portion)}
            </span>
          )}
        </div>
        <div className="font-mono text-xs text-zinc-700">
          <span className={over ? "text-red-600 font-bold" : ""}>{nzd(cat.actual)}</span>
          <span className="text-zinc-400"> / {nzd(cat.budgeted)}</span>
        </div>
      </div>
      <div className="h-2 w-full bg-zinc-100 border border-zinc-200 relative overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      {cat.budgeted > 0 && (
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          {cat.pct_spent}% · {nzd(cat.remaining)} remaining
        </div>
      )}
    </div>
  );
}

function CostRow({ cost, jobId, onChange }) {
  const remove = async () => {
    if (!window.confirm("Delete this cost entry?")) return;
    try {
      await api.delete(`/jobs/${jobId}/costs/${cost.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 p-3 bg-white hover:bg-zinc-50" data-testid={`cost-row-${cost.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{cost.description}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
          {cost.category_key} · {cost.incurred_on}
          {cost.supplier ? ` · ${cost.supplier}` : ""}
          {cost.invoice_ref ? ` · ${cost.invoice_ref}` : ""}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display font-bold">{nzdCents(cost.amount)}</div>
        <button
          onClick={remove}
          data-testid={`cost-delete-${cost.id}`}
          className="text-[10px] text-red-600 hover:text-red-700 font-bold uppercase tracking-widest mt-1">
          <Trash2 className="w-3 h-3 inline mr-0.5" /> delete
        </button>
      </div>
    </div>
  );
}

function EditBudgetDialog({ jobId, budget, onClose, onSaved }) {
  const [total, setTotal] = useState(budget.total_budget?.toString() || "");
  const [note, setNote] = useState(budget.note || "");
  const [rows, setRows] = useState(
    budget.categories.map((c) => ({
      key: c.key, label: c.label,
      budgeted: c.budgeted?.toString() || "",
    }))
  );
  const [resetAlerts, setResetAlerts] = useState(false);
  const [saving, setSaving] = useState(false);

  const sumCats = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.budgeted) || 0), 0),
    [rows]
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        total: parseFloat(total) || 0,
        note: note || null,
        reset_alerts: resetAlerts,
        categories: rows.map((r) => ({ key: r.key, budgeted: parseFloat(r.budgeted) || 0 })),
      };
      const { data } = await api.put(`/jobs/${jobId}/budget`, payload);
      toast.success("Budget saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit budget</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Total budget (NZD)</Label>
            <Input
              type="number" step="0.01" value={total}
              onChange={(e) => setTotal(e.target.value)}
              data-testid="budget-total-input"
              className="border-2 border-black mt-1"
            />
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              Sum of category rows: {nzd(sumCats)}
              {sumCats > 0 && Math.abs(sumCats - (parseFloat(total) || 0)) > 0.01 && (
                  <span className="ml-2 text-amber-700">
                    &#9888; Split doesn&apos;t equal total.
                  </span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Category split (optional)</Label>
            <div className="mt-2 space-y-2">
              {rows.map((r, i) => (
                <div key={r.key} className="flex items-center gap-2">
                  <div className="flex-1 text-sm font-semibold">{r.label}</div>
                  <Input
                    type="number" step="0.01" value={r.budgeted}
                    onChange={(e) => {
                      const next = [...rows];
                      next[i] = { ...next[i], budgeted: e.target.value };
                      setRows(next);
                    }}
                    data-testid={`budget-cat-input-${r.key}`}
                    className="border-2 border-black w-32"
                    placeholder="0.00"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Note (optional)</Label>
            <Input
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="budget-note-input"
              placeholder="e.g. Rev 3 after client uplift"
              className="border-2 border-black mt-1"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox" checked={resetAlerts}
              onChange={(e) => setResetAlerts(e.target.checked)}
              data-testid="budget-reset-alerts-input"
              className="w-4 h-4"
            />
            Reset alert markers (fire fresh alerts if new thresholds cross)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-2 border-black">
            Cancel
          </Button>
          <Button
            onClick={save} disabled={saving}
            data-testid="budget-save-btn"
            className="bg-black text-white hover:bg-zinc-800">
            {saving ? "Saving…" : "Save budget"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCostDialog({ jobId, categories, initialCategory, onClose, onSaved }) {
  const [form, setForm] = useState({
    category_key: categories.some(c => c.key === initialCategory) ? initialCategory : categories[0]?.key || "overheads",
    cost_code_id: "",
    resource_type: "",
    description: "",
    amount: "",
    incurred_on: new Date().toISOString().slice(0, 10),
    supplier: "",
    invoice_ref: "",
    notes: "",
  });
  const [costCodes, setCostCodes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/cost-codes")
      .then(({ data }) => setCostCodes(data.cost_codes || []))
      .catch(() => {});
  }, []);

  const RESOURCES = [
    { v: "L", label: "L · Labour" },
    { v: "P", label: "P · Plant / Machinery" },
    { v: "M", label: "M · Materials" },
    { v: "S", label: "S · Subcontractors" },
  ];

  const save = async () => {
    if (!form.description.trim()) return toast.error("Description required");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Amount required");
    if (form.cost_code_id && !form.resource_type) return toast.error("Pick a resource type (L/P/M/S) for the cost code");
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/costs`, {
        ...form,
        cost_code_id: form.cost_code_id || null,
        resource_type: form.resource_type || null,
        amount: parseFloat(form.amount),
      });
      toast.success("Cost added");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-cost-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add cost entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {costCodes.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Activity code</Label>
                <Select
                  value={form.cost_code_id || "none"}
                  onValueChange={(v) => setForm({ ...form, cost_code_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-2 border-black mt-1" data-testid="cost-code-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No code</SelectItem>
                    {costCodes.map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.code} · {cc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Resource (L/P/M/S)</Label>
                <Select
                  value={form.resource_type || "none"}
                  onValueChange={(v) => setForm({ ...form, resource_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-2 border-black mt-1" data-testid="cost-resource-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {RESOURCES.map((r) => (
                      <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {!form.resource_type && (
            <div>
              <Label className="text-xs uppercase tracking-widest">Category</Label>
              <Select
                value={form.category_key}
                onValueChange={(v) => setForm({ ...form, category_key: v })}>
                <SelectTrigger className="border-2 border-black mt-1" data-testid="cost-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest">Description</Label>
            <Input
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Fulton Hogan invoice 3421"
              data-testid="cost-description-input"
              className="border-2 border-black mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Amount (NZD)</Label>
              <Input
                type="number" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="cost-amount-input"
                className="border-2 border-black mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Date</Label>
              <Input
                type="date" value={form.incurred_on}
                onChange={(e) => setForm({ ...form, incurred_on: e.target.value })}
                data-testid="cost-date-input"
                className="border-2 border-black mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Supplier</Label>
              <Input
                value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                data-testid="cost-supplier-input"
                className="border-2 border-black mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Invoice ref</Label>
              <Input
                value={form.invoice_ref} onChange={(e) => setForm({ ...form, invoice_ref: e.target.value })}
                data-testid="cost-invref-input"
                className="border-2 border-black mt-1"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-2 border-black">
            Cancel
          </Button>
          <Button
            onClick={save} disabled={saving}
            data-testid="cost-save-btn"
            className="bg-black text-white hover:bg-zinc-800">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
